import React from "react";
import cx from "classnames";

import { CityInfo } from "api/requests/getCityStateGeocode";
import { Action, Category } from "constants/events";
import { DeliveryCities, FAQ } from "custom-types/Directus";
import { DispensaryV3 } from "custom-types/DispensariesV3";
import { useEventTracker } from "hooks/useEventTracker";

import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import DealsTakeover from "components/DealsTakeover";
import AddressCaptureModal from "components/DeliveryGateway/AddressCaptureModal";
import DeliveryFromLeafly from "components/DeliveryGateway/DeliveryFromLeafly";
import DeliveryGatewayHero from "components/DeliveryGateway/DeliveryGatewayHero";
import PreaddressDispensaryCards from "components/DeliveryGateway/PreaddressDispensaryCards";
import PreaddressMap from "components/DeliveryGateway/PreaddressMap";
import ReadyForDelivery from "components/DeliveryGateway/ReadyForDelivery";
import DirectusFAQs from "components/DirectusFAQs";
import PopularCities from "components/PopularCities/PopularCities";
import TrackImpression from "components/TrackImpression";

import DeliveryCopyBlockWrapper from "../DeliveryCopyBlock/DeliveryCopyBlockWrapper";

type Props = {
  dispensaries: DispensaryV3[];
  deliveryCities: DeliveryCities["delivery_cities"];
  geoCityResult?: CityInfo;
  metaData?: FAQ;
  setDispensaryLoadLock: (lock: boolean) => void;
  setAttemptedDispensaryName: (name: string) => void;
};

const PreaddressView = ({
  deliveryCities,
  dispensaries,
  geoCityResult,
  metaData,
  setAttemptedDispensaryName,
  setDispensaryLoadLock,
}: Props) => {
  const [modalVisible, setModalVisible] = React.useState(false);
  const modalReturnRef = React.createRef<HTMLButtonElement>();

  const { trackEvent } = useEventTracker();

  const hasGeoState = geoCityResult?.stateLong && geoCityResult?.stateSlug;
  const hasGeoCity = geoCityResult?.city && geoCityResult?.citySlug;

  return (
    <>
      <DeliveryGatewayHero />
      <PreaddressDispensaryCards
        dispensaries={dispensaries}
        geoCityResult={geoCityResult}
        headingOverride={metaData?.h1_override}
        setDispensaryLoadLock={setDispensaryLoadLock}
        setAttemptedDispensaryName={setAttemptedDispensaryName}
      />
      <DeliveryCopyBlockWrapper collection="delivery_copy_block" />
      {!!dispensaries.length && (
        <PreaddressMap
          deliveryCount={dispensaries.length}
          cityInfo={geoCityResult}
        />
      )}
      <DeliveryFromLeafly
        className={cx("mb-xl -mt-lg", { mapOffset: !!dispensaries.length })}
        showCTA={true}
      />

      {!!dispensaries.length && (
        <>
          <AddressCaptureModal
            isVisible={modalVisible}
            onDismissed={() => setModalVisible(false)}
            returnFocusRef={modalReturnRef}
          />
          <DealsTakeover
            ctaLabel="shop deals in your area"
            gaCategory={Category.deliveryGateFlow}
            gaLabel="deals block"
            signupSource="delivery_deals"
            handleClick={() => setModalVisible(true)}
            hideLocation={true}
          />
        </>
      )}
      <TrackImpression category={Category.deliveryGateFlow} label="faq block">
        <DirectusFAQs
          className="my-xxl"
          collection="faq"
          initialShowPanel={true}
          onClick={() =>
            trackEvent(Category.deliveryGateFlow, Action.click, "faq block")
          }
        />
      </TrackImpression>
      <ReadyForDelivery />
      <PopularCities
        cities={deliveryCities}
        title="Weed delivery near you"
        subtitle="Shop Leafly delivery for local, legal weed, anywhere you are."
        className="mt-xl mb-xxl"
        gaCategory={Category.deliveryGateFlow}
        gaLabel="location block"
      />
      {
        /*
        breadcrumbs for SEO when on a city/state page
        we are intentionally linking the last breadcrumb for extra SEO juice
      */
        hasGeoState && (
          <Breadcrumbs>
            <Breadcrumb href="/delivery">Delivery</Breadcrumb>
            <Breadcrumb
              currentLocation={!hasGeoCity}
              href={`/delivery/${geoCityResult.stateSlug}`}
            >
              {geoCityResult.stateLong}
            </Breadcrumb>
            {hasGeoCity && (
              <Breadcrumb
                currentLocation={true}
                href={`/delivery/${geoCityResult.stateSlug}/${geoCityResult.citySlug}`}
              >
                {geoCityResult.city}
              </Breadcrumb>
            )}
          </Breadcrumbs>
        )
      }
      <style jsx>{`
        .mapOffset {
          margin-top: -64px;
        }
      `}</style>
    </>
  );
};

export default PreaddressView;
